import React, { useState, useMemo, useEffect } from "react";
import { C } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, useToast
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  getMeetings, createMeeting, deleteMeeting,
  getCampEditions, getCampLocations, getCampCommittees, getCampCommitteeMembers,
  getCampTasks, getCampVolunteers, getCampProcurement, getCampVendors,
  getCampPartners, getCampLetters, getCampPermits, getCampDawahPrograms,
  getCampNewMuslims, getCampOfficialVisits, getCampLibrary, getCampLessonsLearned,
  createCampEdition, createCampLocation, createCampCommittee, createCampCommitteeMember,
  createCampTask, createCampVolunteer, createCampProcurement, createCampVendor,
  createCampPartner, createCampLetter, createCampPermit, createCampDawahProgram,
  createCampNewMuslim, createCampOfficialVisit, createCampLibraryItem, createCampLessonLearned
} from "./supabaseClient";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  Tent, LayoutDashboard, MapPin, Users, UserCog,
  Calendar, ClipboardList, Plus, Archive,
  BarChart3, PieChart as PieChartIcon, FileText, Search,
  Heart, ShoppingCart, Briefcase, Handshake, Mail, Shield, Star,
  Megaphone, UserPlus, Award, Library, Lightbulb, Download,
  Trash2, Loader2, AlertCircle
} from "lucide-react";

const TODAY_REF = new Date("2026-06-19");

/* ─────────────────────────────────────────────
   ✦ Row mappers (DB snake_case → UI keys)
   ───────────────────────────────────────────── */
const mapEditionRow = (r) => ({
  id: r.id, code: r.code || "", title: r.name || "", year: r.year ?? "",
  startDate: r.start_date || "", endDate: r.end_date || "", status: r.status || "", notes: r.notes || "",
});
const mapLocationRow = (r) => ({
  id: r.id, editionId: r.edition_id, name: r.name || "", city: r.city || "", district: r.district || "",
  address: r.address || "", capacity: r.capacity ?? "", type: r.type || "", supervisorName: r.supervisor_name || "",
  status: r.status || "", notes: r.notes || "",
});
const mapCommitteeRow = (r) => ({
  id: r.id, editionId: r.edition_id, name: r.name || "", head: r.head_name || "", supervisorContact: r.supervisor_contact || "",
  mission: r.mandate || "", status: r.status || "", notes: r.notes || "",
});
const mapMemberRow = (r) => ({
  id: r.id, editionId: r.edition_id, committeeId: r.committee_id || "", committeeName: r.committee_name || "",
  memberName: r.member_name || "", role: r.role || "", phone: r.phone || "", email: r.email || "",
  joinDate: r.join_date || "", status: r.status || "",
});
const mapTaskRow = (r) => ({
  id: r.id, editionId: r.edition_id, committeeId: r.committee_id || "", title: r.title || "", assigneeName: r.assignee_id || "",
  priority: r.priority || "", startDate: r.start_date || "", dueDate: r.due_date || "", status: r.status || "",
  progress: r.progress ?? 0, notes: r.notes || "",
});
const mapVolunteerRow = (r) => ({
  id: r.id, editionId: r.edition_id, name: r.name || "", gender: r.gender || "", phone: r.phone || "", email: r.email || "",
  committeeId: r.committee_id || "", committeeName: r.committee_name || "", role: r.role || "", hours: r.hours ?? 0,
  joinDate: r.join_date || "", certificateIssued: r.certificate_issued ?? false, status: r.status || "",
});
const mapProcurementRow = (r) => ({
  id: r.id, editionId: r.edition_id, item: r.item || "", category: r.category || "", quantity: r.quantity ?? 0, unit: r.unit || "",
  vendorId: r.vendor_id || "", vendorName: r.vendor_name || "", orderDate: r.order_date || "", deliveryDate: r.delivery_date || "",
  totalCost: r.cost ?? 0, status: r.status || "", notes: r.notes || "",
});
const mapVendorRow = (r) => ({
  id: r.id, editionId: r.edition_id, name: r.name || "", type: r.category || "", service: r.service || "",
  contactPerson: r.contact || "", phone: r.phone || "", email: r.email || "", idOrCR: r.id_or_cr || "",
  contractTotal: r.contract_total ?? 0, contractStart: r.contract_start || "", contractEnd: r.contract_end || "",
  rating: r.rating ?? 0, status: r.status || "", notes: r.terms || "",
});
const mapPartnerRow = (r) => ({
  id: r.id, editionId: r.edition_id, name: r.name || "", type: r.type || "", contactPerson: r.contact || "", phone: r.phone || "",
  partnershipType: r.contribution || "", valueOrAmount: r.value_or_amount ?? 0, mou: r.mou ?? false, mouRef: r.mou_ref || "",
  status: r.status || "", notes: r.notes || "",
});
const mapLetterRow = (r) => ({
  id: r.id, editionId: r.edition_id, refNumber: r.ref_number || "", date: r.sent_date || "", direction: r.direction || "",
  recipient: r.party || "", sender: r.sender || "", subject: r.subject || "", summary: r.summary || "",
  attachmentRef: r.attachment_ref || "", status: r.status || "", notes: r.notes || "",
});
const mapPermitRow = (r) => ({
  id: r.id, editionId: r.edition_id, type: r.type || "", authority: r.issuer || "", permitNumber: r.permit_number || "",
  issueDate: r.issued_at || "", expiryDate: r.expiry_date || "", fees: r.fees ?? 0, status: r.status || "",
  conditions: r.conditions || "", notes: r.notes || "",
});
const mapDawahProgramRow = (r) => ({
  id: r.id, editionId: r.edition_id, name: r.name || "", type: r.type || "", speaker: r.speaker || "", locationId: r.location_id || "",
  locationName: r.location_name || "", date: r.date || "", time: r.time || "", attendees: r.attendees_count ?? 0,
  newMuslimsCount: r.new_muslims_count ?? 0, notes: r.notes || "",
});
const mapNewMuslimRow = (r) => ({
  id: r.id, editionId: r.edition_id, nameBefore: r.name_before || "", nameAfter: r.name || "", gender: r.gender || "",
  nationality: r.nationality || "", conversionDate: r.shahada_date || "", locationId: r.location_id || "", locationName: r.location_name || "",
  programId: r.program_id || "", programName: r.program_name || "", preacher: r.preacher || "",
  certificateIssued: r.certificate_issued ?? false, followUpStatus: r.follow_up_status || "", notes: r.notes || "",
});
const mapOfficialVisitRow = (r) => ({
  id: r.id, editionId: r.edition_id, visitorName: r.visitor || "", visitorTitle: r.visitor_title || "", organization: r.organization || "",
  date: r.visit_date || "", time: r.time || "", hostName: r.host_name || "", purpose: r.purpose || "", outcome: r.summary || "",
  mediaCoverage: r.media_coverage ?? false, notes: r.notes || "",
});
const mapLibraryRow = (r) => ({
  id: r.id, editionId: r.edition_id, title: r.title || "", category: r.type || "", uploadDate: r.upload_date || "",
  uploader: r.author || "", fileRef: r.file_ref || "", size: r.size || "", downloads: r.downloads ?? 0, notes: r.notes || "",
});
const mapLessonRow = (r) => ({
  id: r.id, editionId: r.edition_id, category: r.category || "", title: r.title || "", situation: r.situation || "",
  lesson: r.lesson || "", recommendation: r.recommendation || "", owner: r.recorded_by || "", status: r.status || "", notes: r.notes || "",
});

/* ═════════════════════════════════════════════
   ✦ Edition (نسخة سنوية)
   ═════════════════════════════════════════════ */
const EDITION_STATUSES = [
  { key: "مؤرشفة", label: "مؤرشفة", color: C.textLight },
  { key: "منتهية", label: "منتهية", color: "#7c3aed" },
  { key: "جارية",  label: "جارية",  color: C.success },
  { key: "قادمة",  label: "قادمة",  color: C.teal },
];


/* ─────────────────────────────────────────────
   ✦ Locations
   ───────────────────────────────────────────── */
const LOCATION_TYPES = ["رئيسي", "فرعي"];
const LOCATION_STATUSES = [
  { key: "مقترح",     label: "مقترح",      color: C.textLight },
  { key: "معتمد",     label: "معتمد",      color: C.teal },
  { key: "جاهز",      label: "جاهز",       color: C.accent },
  { key: "قيد التشغيل", label: "قيد التشغيل", color: C.success },
  { key: "مغلق",      label: "مغلق",       color: C.danger },
];


const INITIAL_LOCATION = { name: "", city: "بريدة", district: "", address: "", capacity: "", type: "رئيسي", supervisorName: "", status: "مقترح", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Committees
   ───────────────────────────────────────────── */
const COMMITTEE_STATUSES = [
  { key: "نشطة",   label: "نشطة",   color: C.success },
  { key: "منتهية", label: "منتهية", color: "#7c3aed" },
  { key: "متوقفة", label: "متوقفة", color: C.danger },
];


const INITIAL_COMMITTEE = { name: "", head: "", supervisorContact: "", mission: "", status: "نشطة", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Committee Members
   ───────────────────────────────────────────── */
const MEMBER_ROLES = ["رئيس", "نائب", "منسق", "عضو"];
const MEMBER_STATUSES = [
  { key: "نشط",    label: "نشط",    color: C.success },
  { key: "موقوف",  label: "موقوف",  color: C.warning },
  { key: "منسحب",  label: "منسحب",  color: C.danger },
];


const INITIAL_MEMBER = { committeeId: "", memberName: "", role: "عضو", phone: "", email: "", joinDate: "", status: "نشط" };

/* ─────────────────────────────────────────────
   ✦ Meetings — CRUD حقيقي على جدول meetings.
   لكل نسخة سنوية اجتماعاتها المنفصلة عبر module=`camp-${editionId}`.
   ───────────────────────────────────────────── */
const INITIAL_MEETING = { title: "", date: "", time: "", attendees: "", agenda: "", minutesRef: "" };

/* ─────────────────────────────────────────────
   ✦ Tasks
   ───────────────────────────────────────────── */
const TASK_PRIORITIES = [
  { key: "عاجلة",  label: "عاجلة",  color: C.danger },
  { key: "عادية",  label: "عادية",  color: C.teal },
  { key: "منخفضة", label: "منخفضة", color: C.textLight },
];
const TASK_STATUSES = [
  { key: "مفتوحة",      label: "مفتوحة",      color: C.teal },
  { key: "قيد التنفيذ", label: "قيد التنفيذ", color: C.accent },
  { key: "منتظرة",      label: "منتظرة",      color: C.warning },
  { key: "مكتملة",      label: "مكتملة",      color: C.success },
  { key: "متأخرة",      label: "متأخرة",      color: C.danger },
];


const INITIAL_TASK = { committeeId: "", title: "", assigneeName: "", priority: "عادية", startDate: "", dueDate: "", status: "مفتوحة", progress: 0, notes: "" };

/* ─────────────────────────────────────────────
   ✦ Volunteers
   ───────────────────────────────────────────── */
const VOLUNTEER_STATUSES = [
  { key: "نشط",    label: "نشط",    color: C.success },
  { key: "منسحب",  label: "منسحب",  color: C.danger },
  { key: "منتهي",  label: "منتهي",  color: C.textLight },
];
const VOLUNTEER_ROLES = ["منسق ميداني", "ضيافة", "إعلام", "تموين", "استقبال", "متابعة", "عام"];
const GENDERS = ["ذكر", "أنثى"];


const INITIAL_VOLUNTEER = { name: "", gender: "ذكر", phone: "", email: "", committeeId: "", role: "عام", hours: 0, joinDate: "", certificateIssued: false, status: "نشط" };

/* ─────────────────────────────────────────────
   ✦ Procurement
   ───────────────────────────────────────────── */
const PROCUREMENT_CATEGORIES = ["طعام", "خيام", "معدات", "أدوات دعوية", "أخرى"];
const PROCUREMENT_STATUSES = [
  { key: "مطلوب",     label: "مطلوب",     color: C.textLight },
  { key: "قيد الشراء", label: "قيد الشراء", color: C.accent },
  { key: "تم",        label: "تم",         color: C.success },
  { key: "مرتجع",     label: "مرتجع",      color: C.danger },
];


const INITIAL_PROCUREMENT = { item: "", category: "طعام", quantity: 0, unit: "", vendorId: "", vendorName: "", orderDate: "", deliveryDate: "", totalCost: 0, status: "مطلوب", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Vendors (موردون ومناديب)
   ───────────────────────────────────────────── */
const VENDOR_TYPES = ["مندوب", "مورد", "كلاهما"];
const VENDOR_STATUSES = [
  { key: "نشط",         label: "نشط",         color: C.success },
  { key: "منتهي",       label: "منتهي",       color: C.textLight },
  { key: "قائمة سوداء",  label: "قائمة سوداء",  color: C.danger },
];


const INITIAL_VENDOR = { name: "", type: "مورد", service: "", contactPerson: "", phone: "", email: "", idOrCR: "", contractTotal: 0, contractStart: "", contractEnd: "", rating: 3, status: "نشط", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Partners
   ───────────────────────────────────────────── */
const PARTNER_TYPES = ["حكومية", "خيرية", "تجارية", "إعلامية"];
const PARTNERSHIP_TYPES = ["دعم مالي", "دعم لوجستي", "إعلام", "تطوع", "أخرى"];
const PARTNER_STATUSES = [
  { key: "نشط",          label: "نشط",          color: C.success },
  { key: "منتهي",        label: "منتهي",        color: C.textLight },
  { key: "قيد التفاوض",  label: "قيد التفاوض",  color: C.warning },
];


const INITIAL_PARTNER = { name: "", type: "حكومية", contactPerson: "", phone: "", partnershipType: "دعم مالي", valueOrAmount: 0, mou: false, mouRef: "", status: "نشط", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Letters
   ───────────────────────────────────────────── */
const LETTER_DIRECTIONS = ["صادر", "وارد"];
const LETTER_STATUSES = [
  { key: "مرسل",   label: "مرسل",   color: C.teal },
  { key: "مستلم",  label: "مستلم",  color: C.success },
  { key: "معلق",   label: "معلق",   color: C.warning },
  { key: "مؤرشف",  label: "مؤرشف",  color: C.textLight },
];


const INITIAL_LETTER = { refNumber: "", date: "", direction: "صادر", recipient: "", sender: "", subject: "", summary: "", attachmentRef: "", status: "مرسل", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Permits
   ───────────────────────────────────────────── */
const PERMIT_TYPES = ["بلدية", "أمنية", "دفاع مدني", "تجارية", "أخرى"];
const PERMIT_STATUSES = [
  { key: "قيد التقديم", label: "قيد التقديم", color: C.warning },
  { key: "معتمد",       label: "معتمد",       color: C.success },
  { key: "مرفوض",       label: "مرفوض",       color: C.danger },
  { key: "منتهي",       label: "منتهي",       color: C.textLight },
];


const INITIAL_PERMIT = { type: "بلدية", authority: "", permitNumber: "", issueDate: "", expiryDate: "", fees: 0, status: "قيد التقديم", conditions: "", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Dawah Programs (البرامج الدعوية)
   ───────────────────────────────────────────── */
const DAWAH_PROGRAM_TYPES = ["كلمة", "درس", "محاضرة", "مسابقة", "معرض"];


const INITIAL_DAWAH_PROGRAM = { name: "", type: "كلمة", speaker: "", locationId: "", locationName: "", date: "", time: "", attendees: 0, newMuslimsCount: 0, notes: "" };

/* ─────────────────────────────────────────────
   ✦ New Muslims (المسلمون الجدد)
   ───────────────────────────────────────────── */
const NEW_MUSLIM_FOLLOWUP_STATUSES = [
  { key: "تحت المتابعة", label: "تحت المتابعة", color: C.accent },
  { key: "مكتمل",        label: "مكتمل",        color: C.success },
  { key: "منقطع",        label: "منقطع",        color: C.danger },
];


const INITIAL_NEW_MUSLIM = { nameBefore: "", nameAfter: "", gender: "ذكر", nationality: "", conversionDate: "", locationId: "", locationName: "", programId: "", programName: "", preacher: "", certificateIssued: false, followUpStatus: "تحت المتابعة", notes: "" };

/* ─────────────────────────────────────────────
   ✦ Official Visits (الزيارات الرسمية)
   ───────────────────────────────────────────── */

const INITIAL_OFFICIAL_VISIT = { visitorName: "", visitorTitle: "", organization: "", date: "", time: "", hostName: "", purpose: "", outcome: "", mediaCoverage: false, notes: "" };

/* ─────────────────────────────────────────────
   ✦ Library & Archive (المكتبة والأرشيف)
   ───────────────────────────────────────────── */
const LIBRARY_CATEGORIES = ["محاضر", "كتيبات", "تصاميم", "صور", "فيديو", "مستندات"];


const INITIAL_LIBRARY = { title: "", category: "محاضر", uploadDate: "", uploader: "", fileRef: "", size: "", downloads: 0, notes: "" };

/* ─────────────────────────────────────────────
   ✦ Lessons Learned (الدروس المستفادة)
   ───────────────────────────────────────────── */
const LESSON_CATEGORIES = ["تشغيل", "لوجستي", "مالي", "تنظيمي", "دعوي"];
const LESSON_STATUSES = [
  { key: "مفتوحة", label: "مفتوحة", color: C.accent },
  { key: "مطبقة",  label: "مطبقة",  color: C.success },
  { key: "مؤرشفة", label: "مؤرشفة", color: C.textLight },
];


const INITIAL_LESSON = { category: "تشغيل", title: "", situation: "", lesson: "", recommendation: "", owner: "", status: "مفتوحة", notes: "" };

/* ═════════════════════════════════════════════
   ✦ CampPage — مخيم فطروهم (المرحلة 10 — 18 تبويباً)
   ═════════════════════════════════════════════ */
export default function CampPage() {
  const [tab, setTab] = useState("overview");
  const [editions, setEditions] = useState([]);
  const [activeEditionId, setActiveEditionId] = useState("ed-13");
  const [showNewEdition, setShowNewEdition] = useState(false);
  const [showToast, ToastView] = useToast();

  const [locations, setLocations] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [procurement, setProcurement] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [partners, setPartners] = useState([]);
  const [letters, setLetters] = useState([]);
  const [permits, setPermits] = useState([]);
  const [dawahPrograms, setDawahPrograms] = useState([]);
  const [newMuslims, setNewMuslims] = useState([]);
  const [officialVisits, setOfficialVisits] = useState([]);
  const [library, setLibrary] = useState([]);
  const [lessonsLearned, setLessonsLearned] = useState([]);

  useEffect(() => {
    getCampEditions().then(({ data }) => {
      const mapped = (data || []).map(mapEditionRow);
      setEditions(mapped);
      if (mapped.length) setActiveEditionId(prev => (mapped.some(e => e.id === prev) ? prev : mapped[0].id));
    }).catch(() => {});
    getCampLocations().then(({ data }) => setLocations((data || []).map(mapLocationRow))).catch(() => {});
    getCampCommittees().then(({ data }) => setCommittees((data || []).map(mapCommitteeRow))).catch(() => {});
    getCampCommitteeMembers().then(({ data }) => setMembers((data || []).map(mapMemberRow))).catch(() => {});
    getCampTasks().then(({ data }) => setTasks((data || []).map(mapTaskRow))).catch(() => {});
    getCampVolunteers().then(({ data }) => setVolunteers((data || []).map(mapVolunteerRow))).catch(() => {});
    getCampProcurement().then(({ data }) => setProcurement((data || []).map(mapProcurementRow))).catch(() => {});
    getCampVendors().then(({ data }) => setVendors((data || []).map(mapVendorRow))).catch(() => {});
    getCampPartners().then(({ data }) => setPartners((data || []).map(mapPartnerRow))).catch(() => {});
    getCampLetters().then(({ data }) => setLetters((data || []).map(mapLetterRow))).catch(() => {});
    getCampPermits().then(({ data }) => setPermits((data || []).map(mapPermitRow))).catch(() => {});
    getCampDawahPrograms().then(({ data }) => setDawahPrograms((data || []).map(mapDawahProgramRow))).catch(() => {});
    getCampNewMuslims().then(({ data }) => setNewMuslims((data || []).map(mapNewMuslimRow))).catch(() => {});
    getCampOfficialVisits().then(({ data }) => setOfficialVisits((data || []).map(mapOfficialVisitRow))).catch(() => {});
    getCampLibrary().then(({ data }) => setLibrary((data || []).map(mapLibraryRow))).catch(() => {});
    getCampLessonsLearned().then(({ data }) => setLessonsLearned((data || []).map(mapLessonRow))).catch(() => {});
  }, []);

  const activeEdition = editions.find(e => e.id === activeEditionId) || editions[0];

  const tabs = [
    { key: "overview",       label: "لوحة التحكم",         icon: LayoutDashboard },
    { key: "locations",      label: "المواقع",              icon: MapPin },
    { key: "committees",     label: "اللجان",               icon: Users },
    { key: "members",        label: "أعضاء اللجان",        icon: UserCog },
    { key: "meetings",       label: "الاجتماعات",           icon: Calendar },
    { key: "tasks",          label: "المهام والمتابعة",     icon: ClipboardList },
    { key: "volunteers",     label: "المتطوعون",            icon: Heart },
    { key: "procurement",    label: "التموين والمشتريات",   icon: ShoppingCart },
    { key: "vendors",        label: "المناديب والموردون",   icon: Briefcase },
    { key: "partners",       label: "الجهات الشريكة",       icon: Handshake },
    { key: "letters",        label: "الخطابات",             icon: Mail },
    { key: "permits",        label: "التراخيص والموافقات",   icon: Shield },
    { key: "dawahPrograms",  label: "البرامج الدعوية",      icon: Megaphone },
    { key: "newMuslims",     label: "المسلمون الجدد",       icon: UserPlus },
    { key: "officialVisits", label: "الزيارات الرسمية",     icon: Award },
    { key: "reports",        label: "التقارير",             icon: FileText },
    { key: "library",        label: "المكتبة والأرشيف",     icon: Library },
    { key: "lessonsLearned", label: "الدروس المستفادة",     icon: Lightbulb },
  ];

  const reportSources = [
    {
      label: "البرامج الدعوية", mode: "monthly", dateField: "date", rows: dawahPrograms,
      columns: [
        { key: "name", label: "البرنامج" },
        { key: "type", label: "النوع" },
        { key: "speaker", label: "المُلقي" },
        { key: "locationName", label: "الموقع" },
        { key: "date", label: "التاريخ" },
        { key: "attendees", label: "الحضور" },
      ],
    },
    {
      label: "المسلمون الجدد", mode: "monthly", dateField: "conversionDate", rows: newMuslims,
      columns: [
        { key: "nameAfter", label: "الاسم بعد الإسلام" },
        { key: "nationality", label: "الجنسية" },
        { key: "conversionDate", label: "تاريخ الإسلام" },
        { key: "locationName", label: "الموقع" },
        { key: "preacher", label: "الداعية" },
        { key: "followUpStatus", label: "المتابعة" },
      ],
    },
    {
      label: "الزيارات الرسمية", mode: "monthly", dateField: "date", rows: officialVisits,
      columns: [
        { key: "visitorName", label: "الزائر" },
        { key: "visitorTitle", label: "الصفة" },
        { key: "organization", label: "الجهة" },
        { key: "date", label: "التاريخ" },
        { key: "hostName", label: "المستضيف" },
        { key: "purpose", label: "الغرض" },
      ],
    },
    {
      label: "المتطوعون", mode: "monthly", dateField: "joinDate", rows: volunteers,
      columns: [
        { key: "name", label: "الاسم" },
        { key: "committeeName", label: "اللجنة" },
        { key: "role", label: "الدور" },
        { key: "hours", label: "الساعات" },
        { key: "joinDate", label: "تاريخ الانضمام" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "المشتريات", mode: "monthly", dateField: "orderDate", rows: procurement,
      columns: [
        { key: "item", label: "الصنف" },
        { key: "category", label: "الفئة" },
        { key: "quantity", label: "الكمية" },
        { key: "vendorName", label: "المورّد" },
        { key: "orderDate", label: "تاريخ الطلب" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "المخاطبات", mode: "monthly", dateField: "date", rows: letters,
      columns: [
        { key: "refNumber", label: "رقم المرجع" },
        { key: "direction", label: "الاتجاه" },
        { key: "recipient", label: "الجهة" },
        { key: "subject", label: "الموضوع" },
        { key: "date", label: "التاريخ" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "المهام", mode: "monthly", dateField: "startDate", rows: tasks,
      columns: [
        { key: "title", label: "المهمة" },
        { key: "assigneeName", label: "المسؤول" },
        { key: "priority", label: "الأولوية" },
        { key: "startDate", label: "تاريخ البدء" },
        { key: "dueDate", label: "الاستحقاق" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "المكتبة", mode: "monthly", dateField: "uploadDate", rows: library,
      columns: [
        { key: "title", label: "العنوان" },
        { key: "category", label: "الفئة" },
        { key: "uploader", label: "الرافع" },
        { key: "uploadDate", label: "تاريخ الرفع" },
        { key: "downloads", label: "التحميلات" },
      ],
    },
    { label: "النسخ", mode: "cumulative", rows: editions },
    { label: "المواقع", mode: "cumulative", rows: locations },
    { label: "اللجان", mode: "cumulative", rows: committees },
    { label: "الأعضاء", mode: "cumulative", rows: members },
    { label: "الموردون", mode: "cumulative", rows: vendors },
    { label: "الشركاء", mode: "cumulative", rows: partners },
    { label: "التصاريح", mode: "cumulative", rows: permits },
    { label: "الدروس المستفادة", mode: "cumulative", rows: lessonsLearned },
  ];

  return (
    <div>
      {ToastView}
      <ModuleHeader icon={Tent} title="مخيم فطروهم" subtitle="نظام إدارة النسخ السنوية لمخيم فطروهم">
        <MonthlyReportButton moduleTitle="مخيم فطروهم" sources={reportSources} />
      </ModuleHeader>

      <EditionSelector
        editions={editions}
        activeEditionId={activeEditionId}
        onChange={setActiveEditionId}
        onNew={() => setShowNewEdition(true)}
      />

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview"       && <OverviewTab editions={editions} activeEdition={activeEdition} locations={locations} committees={committees} members={members} tasks={tasks} volunteers={volunteers} dawahPrograms={dawahPrograms} newMuslims={newMuslims} />}
      {tab === "locations"      && <LocationsTab activeEditionId={activeEditionId} locations={locations} setLocations={setLocations} showToast={showToast} />}
      {tab === "committees"     && <CommitteesTab activeEditionId={activeEditionId} committees={committees} members={members} setCommittees={setCommittees} showToast={showToast} />}
      {tab === "members"        && <MembersTab activeEditionId={activeEditionId} committees={committees} members={members} setMembers={setMembers} showToast={showToast} />}
      {tab === "meetings"       && <CampMeetingsView activeEditionId={activeEditionId} showToast={showToast} />}
      {tab === "tasks"          && <TasksTab activeEditionId={activeEditionId} committees={committees} tasks={tasks} setTasks={setTasks} showToast={showToast} />}
      {tab === "volunteers"     && <VolunteersTab activeEditionId={activeEditionId} committees={committees} volunteers={volunteers} setVolunteers={setVolunteers} showToast={showToast} />}
      {tab === "procurement"    && <ProcurementTab activeEditionId={activeEditionId} vendors={vendors} procurement={procurement} setProcurement={setProcurement} showToast={showToast} />}
      {tab === "vendors"        && <VendorsTab activeEditionId={activeEditionId} vendors={vendors} setVendors={setVendors} showToast={showToast} />}
      {tab === "partners"       && <PartnersTab activeEditionId={activeEditionId} partners={partners} setPartners={setPartners} showToast={showToast} />}
      {tab === "letters"        && <LettersTab activeEditionId={activeEditionId} letters={letters} setLetters={setLetters} showToast={showToast} />}
      {tab === "permits"        && <PermitsTab activeEditionId={activeEditionId} permits={permits} setPermits={setPermits} showToast={showToast} />}
      {tab === "dawahPrograms"  && <DawahProgramsTab activeEditionId={activeEditionId} locations={locations} dawahPrograms={dawahPrograms} setDawahPrograms={setDawahPrograms} showToast={showToast} />}
      {tab === "newMuslims"     && <NewMuslimsTab activeEditionId={activeEditionId} locations={locations} dawahPrograms={dawahPrograms} newMuslims={newMuslims} setNewMuslims={setNewMuslims} showToast={showToast} />}
      {tab === "officialVisits" && <OfficialVisitsTab activeEditionId={activeEditionId} officialVisits={officialVisits} setOfficialVisits={setOfficialVisits} showToast={showToast} />}
      {tab === "reports"        && <ReportsTab editions={editions} activeEdition={activeEdition} locations={locations} committees={committees} volunteers={volunteers} procurement={procurement} dawahPrograms={dawahPrograms} newMuslims={newMuslims} showToast={showToast} />}
      {tab === "library"        && <LibraryTab activeEditionId={activeEditionId} library={library} setLibrary={setLibrary} showToast={showToast} />}
      {tab === "lessonsLearned" && <LessonsLearnedTab activeEditionId={activeEditionId} lessonsLearned={lessonsLearned} setLessonsLearned={setLessonsLearned} showToast={showToast} />}

      {showNewEdition && (
        <NewEditionModal
          editions={editions}
          activeEditionId={activeEditionId}
          locations={locations}
          committees={committees}
          vendors={vendors}
          partners={partners}
          onClose={() => setShowNewEdition(false)}
          onCreate={(newEd, imported) => {
            setEditions(prev => [...prev, newEd]);
            if (imported.locations.length)  setLocations(prev  => [...prev, ...imported.locations]);
            if (imported.committees.length) setCommittees(prev => [...prev, ...imported.committees]);
            if (imported.vendors.length)    setVendors(prev    => [...prev, ...imported.vendors]);
            if (imported.partners.length)   setPartners(prev   => [...prev, ...imported.partners]);
            setActiveEditionId(newEd.id);
            setShowNewEdition(false);
            showToast(`تم إنشاء النسخة ${newEd.title} بنجاح`);
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ EditionSelector
   ───────────────────────────────────────────── */
function EditionSelector({ editions, activeEditionId, onChange, onNew }) {
  const active = editions.find(e => e.id === activeEditionId);
  return (
    <Card style={styles.editionCard}>
      <div style={styles.editionRow}>
        <div style={styles.editionInfo}>
          <Archive size={20} color={C.teal} />
          <div>
            <div style={styles.editionLabel}>النسخة النشطة</div>
            <div style={styles.editionTitle}>
              {active ? `${active.title} — ${active.year} هـ` : "—"}
              {active && (
                <span style={{ marginRight: 10 }}>
                  <StatusBadge status={active.status} statuses={EDITION_STATUSES} />
                </span>
              )}
            </div>
          </div>
        </div>
        <div style={styles.editionControls}>
          <select
            value={activeEditionId}
            onChange={e => onChange(e.target.value)}
            style={styles.editionSelect}
          >
            {editions.map(ed => (
              <option key={ed.id} value={ed.id}>{ed.title} — {ed.year} هـ ({ed.status})</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={onNew}>إنشاء نسخة جديدة</Btn>
        </div>
      </div>
    </Card>
  );
}

/* ─────────────────────────────────────────────
   ✦ NewEditionModal
   ───────────────────────────────────────────── */
function NewEditionModal({ editions, activeEditionId, locations, committees, vendors, partners, onClose, onCreate }) {
  const previous = editions.find(e => e.id === activeEditionId);
  const [form, setForm] = useState({
    code: "",
    title: "",
    year: "",
    startDate: "",
    endDate: "",
  });
  const [importFlags, setImportFlags] = useState({
    committees: true,
    locations: true,
    suppliers: true,
    delegates: true,
    partners: true,
    forms: false,
  });
  const [localErr, setLocalErr] = useState("");

  function toggleImport(key) {
    setImportFlags(prev => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleCreate() {
    if (!form.code || !form.title || !form.year || !form.startDate || !form.endDate) {
      setLocalErr("يرجى تعبئة الحقول المطلوبة");
      return;
    }
    const { data: edRow, error: edErr } = await createCampEdition({
      code: form.code,
      name: form.title,
      year: Number(form.year) || 0,
      start_date: form.startDate || null,
      end_date: form.endDate || null,
      status: "قادمة",
      notes: "",
    });
    if (edErr) { setLocalErr(edErr); return; }
    if (!edRow) { setLocalErr("تعذّر إنشاء النسخة"); return; }
    const newEd = mapEditionRow(edRow);
    const newId = newEd.id;

    const importedLocations = [];
    if (importFlags.locations && previous) {
      for (const l of locations.filter(x => x.editionId === previous.id)) {
        const { data } = await createCampLocation({
          edition_id: newId, name: l.name, city: l.city, district: l.district,
          address: l.address, capacity: Number(l.capacity) || 0, type: l.type,
          supervisor_name: l.supervisorName, status: "مقترح", notes: l.notes,
        });
        if (data) importedLocations.push(mapLocationRow(data));
      }
    }

    const importedCommittees = [];
    if (importFlags.committees && previous) {
      for (const c of committees.filter(x => x.editionId === previous.id)) {
        const { data } = await createCampCommittee({
          edition_id: newId, name: c.name, head_name: c.head,
          supervisor_contact: c.supervisorContact, mandate: c.mission,
          status: "نشطة", notes: c.notes,
        });
        if (data) importedCommittees.push(mapCommitteeRow(data));
      }
    }

    const importedVendors = [];
    if (previous && (importFlags.suppliers || importFlags.delegates)) {
      const allowedTypes = new Set();
      if (importFlags.suppliers) { allowedTypes.add("مورد"); allowedTypes.add("كلاهما"); }
      if (importFlags.delegates) { allowedTypes.add("مندوب"); allowedTypes.add("كلاهما"); }
      for (const v of vendors.filter(x => x.editionId === previous.id && allowedTypes.has(x.type))) {
        const { data } = await createCampVendor({
          edition_id: newId, name: v.name, category: v.type, service: v.service,
          contact: v.contactPerson, phone: v.phone, email: v.email, id_or_cr: v.idOrCR,
          contract_total: Number(v.contractTotal) || 0,
          contract_start: v.contractStart || null, contract_end: v.contractEnd || null,
          rating: Number(v.rating) || 0, status: "نشط", terms: v.notes,
        });
        if (data) importedVendors.push(mapVendorRow(data));
      }
    }

    const importedPartners = [];
    if (importFlags.partners && previous) {
      for (const p of partners.filter(x => x.editionId === previous.id)) {
        const { data } = await createCampPartner({
          edition_id: newId, name: p.name, type: p.type, contact: p.contactPerson,
          phone: p.phone, contribution: p.partnershipType,
          value_or_amount: Number(p.valueOrAmount) || 0, mou: p.mou, mou_ref: p.mouRef,
          status: "قيد التفاوض", notes: p.notes,
        });
        if (data) importedPartners.push(mapPartnerRow(data));
      }
    }

    onCreate(newEd, {
      locations:  importedLocations,
      committees: importedCommittees,
      vendors:    importedVendors,
      partners:   importedPartners,
    });
  }

  const importOptions = [
    { key: "committees", label: "اللجان",                ready: true },
    { key: "locations",  label: "المواقع",               ready: true },
    { key: "suppliers",  label: "الموردين",              ready: true },
    { key: "delegates",  label: "المناديب",              ready: true },
    { key: "partners",   label: "الجهات الشريكة",        ready: true },
    { key: "forms",      label: "النماذج والقوالب",     ready: true },
  ];

  return (
    <Modal title="إنشاء نسخة جديدة" onClose={onClose} width={680}>
      <div style={styles.formGrid2}>
        <Field label="كود النسخة" value={form.code} onChange={v => setForm({ ...form, code: v })} placeholder="مثال: 14" required />
        <Field label="عنوان النسخة" value={form.title} onChange={v => setForm({ ...form, title: v })} placeholder="مثال: فطروهم 14" required />
        <Field label="السنة الهجرية" type="number" value={form.year} onChange={v => setForm({ ...form, year: v })} placeholder="1448" required />
        <div />
        <Field label="تاريخ البداية" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} required />
        <Field label="تاريخ النهاية" type="date" value={form.endDate} onChange={v => setForm({ ...form, endDate: v })} required />
      </div>

      <div style={styles.sectionDivider}>الاستيراد الانتقائي من النسخة السابقة</div>
      <p style={styles.helperText}>
        يتم النسخ من النسخة النشطة الحالية: {previous ? previous.title : "—"}.
        {" "}
        خيار "النماذج والقوالب" يفعّل علماً بالحالة فقط — لا توجد قوالب فعلية حالياً
        لينسخ منها النظام، فعند تفعيل القوالب الفعلية مستقبلاً ستُستورد تلقائياً.
      </p>
      <div style={styles.checkboxGrid}>
        {importOptions.map(opt => (
          <label
            key={opt.key}
            style={{
              ...styles.checkboxRow,
              opacity: opt.ready ? 1 : 0.55,
              cursor: opt.ready ? "pointer" : "not-allowed",
            }}
          >
            <input
              type="checkbox"
              checked={importFlags[opt.key]}
              onChange={() => opt.ready && toggleImport(opt.key)}
              disabled={!opt.ready}
              style={{ marginLeft: 8 }}
            />
            <span>{opt.label}{!opt.ready && " (قريباً)"}</span>
          </label>
        ))}
      </div>

      {localErr && <p style={{ color: C.danger, fontSize: 13, marginTop: 12 }}>{localErr}</p>}

      <div style={styles.modalActions}>
        <Btn variant="ghost" onClick={onClose}>إلغاء</Btn>
        <Btn onClick={handleCreate} icon={Plus}>إنشاء</Btn>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   ✦ Overview Tab
   ───────────────────────────────────────────── */
function OverviewTab({ editions, activeEdition, locations, committees, members, tasks, volunteers, dawahPrograms, newMuslims }) {
  const editionId = activeEdition?.id;
  const locs   = locations.filter(l => l.editionId === editionId);
  const coms   = committees.filter(c => c.editionId === editionId);
  const mems   = members.filter(m => m.editionId === editionId);
  const tsks   = tasks.filter(t => t.editionId === editionId);
  const vols   = volunteers.filter(v => v.editionId === editionId);
  const dwhs   = dawahPrograms.filter(p => p.editionId === editionId);
  const nmus   = newMuslims.filter(n => n.editionId === editionId);

  const openTasks = tsks.filter(t => t.status !== "مكتملة").length;
  const totalProg = tsks.length > 0 ? Math.round(tsks.reduce((s, t) => s + (Number(t.progress) || 0), 0) / tsks.length) : 0;

  const taskByStatus = TASK_STATUSES.map(st => ({
    name: st.label,
    value: tsks.filter(t => t.status === st.key).length,
    color: st.color,
  })).filter(d => d.value > 0);

  const newMuslimsAcrossEditions = useMemo(() => editions.map(ed => ({
    name: ed.title,
    value: newMuslims.filter(n => n.editionId === ed.id).length,
    color: ed.id === editionId ? C.teal : C.primary,
  })), [editions, newMuslims, editionId]);

  const volByCommittee = useMemo(() => {
    const palette = ["#0891b2", C.teal, "#7c3aed", C.accent, "#ea580c", C.success, C.danger, C.primary];
    return coms.map((c, i) => ({
      name: c.name,
      value: vols.filter(v => v.committeeId === c.id).length,
      color: palette[i % palette.length],
    })).filter(d => d.value > 0);
  }, [coms, vols]);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="المواقع"           value={locs.length}     icon={MapPin}         color="#0891b2" />
        <KPICard title="اللجان"            value={coms.length}     icon={Users}          color={C.teal} />
        <KPICard title="أعضاء اللجان"      value={mems.length}     icon={UserCog}        color="#7c3aed" />
        <KPICard title="المهام المفتوحة"   value={openTasks}        icon={ClipboardList}  color={C.warning} />
        <KPICard title="المتطوعون"         value={vols.length}      icon={Heart}          color={C.accent} />
        <KPICard title="البرامج الدعوية"   value={dwhs.length}      icon={Megaphone}      color="#ea580c" />
        <KPICard title="المسلمون الجدد"    value={nmus.length}      icon={UserPlus}       color={C.success} />
        <KPICard title="نسبة الجاهزية"     value={`${totalProg}%`}  icon={BarChart3}      color={C.primary} />
      </div>

      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color="#7c3aed" />
              <span style={styles.chartTitle}>توزيع المهام حسب الحالة</span>
            </div>
          </div>
          {taskByStatus.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={taskByStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                    {taskByStatus.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                    formatter={(v) => [`${v} مهمة`]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.legendGrid}>
                {taskByStatus.map((d, i) => (
                  <div key={i} style={styles.legendItem}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                    <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.emptyChart}>لا توجد مهام للنسخة الحالية بعد.</div>
          )}
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.teal} />
              <span style={styles.chartTitle}>مقارنة المسلمين الجدد بين النسخ</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={newMuslimsAcrossEditions} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" fontSize={11} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} مسلم جديد`]}
              />
              <Bar dataKey="value" name="المسلمون الجدد" radius={[6, 6, 0, 0]}>
                {newMuslimsAcrossEditions.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color={C.accent} />
              <span style={styles.chartTitle}>توزيع المتطوعين على اللجان</span>
            </div>
          </div>
          {volByCommittee.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={volByCommittee} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                    {volByCommittee.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                    formatter={(v) => [`${v} متطوع`]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.legendGrid}>
                {volByCommittee.map((d, i) => (
                  <div key={i} style={styles.legendItem}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                    <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.emptyChart}>لا توجد بيانات متطوعين للنسخة الحالية بعد.</div>
          )}
        </Card>
      </div>

      <Card style={{ marginTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <FileText size={16} color={C.teal} />
          <span style={styles.chartTitle}>تفاصيل النسخة النشطة</span>
        </div>
        <div style={styles.detailGrid}>
          <div><span style={styles.dimLabel}>الكود:</span> {activeEdition?.code || "—"}</div>
          <div><span style={styles.dimLabel}>السنة:</span> {activeEdition?.year || "—"} هـ</div>
          <div><span style={styles.dimLabel}>البداية:</span> {activeEdition?.startDate || "—"}</div>
          <div><span style={styles.dimLabel}>النهاية:</span> {activeEdition?.endDate || "—"}</div>
          <div><span style={styles.dimLabel}>الحالة:</span> <StatusBadge status={activeEdition?.status} statuses={EDITION_STATUSES} /></div>
          <div><span style={styles.dimLabel}>تاريخ المرجع:</span> {TODAY_REF.toISOString().slice(0, 10)}</div>
        </div>
        {activeEdition?.notes && <p style={{ margin: "10px 0 0", color: C.textLight, fontSize: 13 }}>{activeEdition.notes}</p>}
      </Card>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Locations Tab
   ───────────────────────────────────────────── */
function LocationsTab({ activeEditionId, locations, setLocations, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LOCATION);
  const [search, setSearch] = useState("");

  const filtered = locations
    .filter(l => l.editionId === activeEditionId)
    .filter(l => !search || l.name.includes(search) || l.district.includes(search));

  async function handleSave() {
    if (!form.name || !form.city || !form.district) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampLocation({
      edition_id: activeEditionId,
      name: form.name, city: form.city, district: form.district,
      address: form.address, capacity: Number(form.capacity) || 0, type: form.type,
      supervisor_name: form.supervisorName, status: form.status, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setLocations(prev => [mapLocationRow(data), ...prev]);
    setForm(INITIAL_LOCATION);
    setShowAdd(false);
    showToast("تم إضافة الموقع بنجاح");
  }

  const columns = [
    { key: "name",     label: "الاسم", nowrap: true },
    { key: "district", label: "الحي" },
    { key: "type",     label: "النوع", nowrap: true },
    { key: "capacity", label: "السعة", nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "supervisorName", label: "المشرف" },
    { key: "status",   label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={LOCATION_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الحي..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة موقع</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة موقع جديد" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="اسم الموقع" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="المدينة"    value={form.city} onChange={v => setForm({ ...form, city: v })} required />
            <Field label="الحي"       value={form.district} onChange={v => setForm({ ...form, district: v })} required />
            <Field label="العنوان"    value={form.address}  onChange={v => setForm({ ...form, address: v })} />
            <Field label="السعة"      type="number" value={form.capacity} onChange={v => setForm({ ...form, capacity: v })} />
            <Field label="النوع"      type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={LOCATION_TYPES} />
            <Field label="المشرف"     value={form.supervisorName} onChange={v => setForm({ ...form, supervisorName: v })} />
            <Field label="الحالة"     type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={LOCATION_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Committees Tab
   ───────────────────────────────────────────── */
function CommitteesTab({ activeEditionId, committees, members, setCommittees, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_COMMITTEE);

  const filtered = committees.filter(c => c.editionId === activeEditionId);

  function countMembers(committeeId) {
    return members.filter(m => m.committeeId === committeeId).length;
  }

  async function handleSave() {
    if (!form.name || !form.head) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampCommittee({
      edition_id: activeEditionId,
      name: form.name, head_name: form.head, supervisor_contact: form.supervisorContact,
      mandate: form.mission, status: form.status, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setCommittees(prev => [mapCommitteeRow(data), ...prev]);
    setForm(INITIAL_COMMITTEE);
    setShowAdd(false);
    showToast("تم إضافة اللجنة بنجاح");
  }

  const columns = [
    { key: "name",   label: "اللجنة", nowrap: true },
    { key: "head",   label: "الرئيس" },
    { key: "supervisorContact", label: "التواصل" },
    { key: "membersCount", label: "الأعضاء", nowrap: true, render: (_, row) => countMembers(row.id) },
    { key: "mission", label: "المهمة" },
    { key: "status",  label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={COMMITTEE_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ color: C.textLight, fontSize: 13 }}>
            {filtered.length} لجنة في النسخة النشطة
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة لجنة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة لجنة جديدة" onClose={() => setShowAdd(false)} width={620}>
          <div style={styles.formGrid2}>
            <Field label="اسم اللجنة" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="رئيس اللجنة" value={form.head} onChange={v => setForm({ ...form, head: v })} required />
            <Field label="تواصل المشرف" value={form.supervisorContact} onChange={v => setForm({ ...form, supervisorContact: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={COMMITTEE_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <Field label="المهمة" type="textarea" value={form.mission} onChange={v => setForm({ ...form, mission: v })} />
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Members Tab
   ───────────────────────────────────────────── */
function MembersTab({ activeEditionId, committees, members, setMembers, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_MEMBER);
  const [filterCommittee, setFilterCommittee] = useState("all");

  const editionCommittees = committees.filter(c => c.editionId === activeEditionId);
  const filtered = members
    .filter(m => m.editionId === activeEditionId)
    .filter(m => filterCommittee === "all" || m.committeeId === filterCommittee);

  async function handleSave() {
    if (!form.committeeId || !form.memberName) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const committee = editionCommittees.find(c => c.id === form.committeeId);
    const { data, error } = await createCampCommitteeMember({
      edition_id: activeEditionId,
      committee_id: form.committeeId, committee_name: committee ? committee.name : "—",
      member_name: form.memberName, role: form.role, phone: form.phone, email: form.email,
      join_date: form.joinDate || null, status: form.status,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setMembers(prev => [mapMemberRow(data), ...prev]);
    setForm(INITIAL_MEMBER);
    setShowAdd(false);
    showToast("تم إضافة العضو بنجاح");
  }

  const columns = [
    { key: "memberName",    label: "العضو", nowrap: true },
    { key: "committeeName", label: "اللجنة" },
    { key: "role",          label: "الدور", nowrap: true },
    { key: "phone",         label: "الجوال", nowrap: true },
    { key: "email",         label: "البريد" },
    { key: "joinDate",      label: "تاريخ الانضمام", nowrap: true },
    { key: "status",        label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={MEMBER_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterCommittee} onChange={e => setFilterCommittee(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع اللجان</option>
            {editionCommittees.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة عضو</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة عضو لجنة" onClose={() => setShowAdd(false)} width={620}>
          <div style={styles.formGrid2}>
            <Field
              label="اللجنة"
              type="select"
              value={form.committeeId}
              onChange={v => setForm({ ...form, committeeId: v })}
              options={editionCommittees.map(c => ({ value: c.id, label: c.name }))}
              required
            />
            <Field label="اسم العضو" value={form.memberName} onChange={v => setForm({ ...form, memberName: v })} required />
            <Field label="الدور" type="select" value={form.role} onChange={v => setForm({ ...form, role: v })} options={MEMBER_ROLES} />
            <Field label="الجوال" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            <Field label="البريد" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            <Field label="تاريخ الانضمام" type="date" value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={MEMBER_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ مساعدات اجتماعات المخيم (CRUD حقيقي، module=`camp-${editionId}`)
   الأعمدة المخزّنة فقط: title/meeting_date/attendees.
   بقية الحقول (time/agenda/minutesRef) عرض-فقط ولا تصمد بعد إعادة التحميل.
   ───────────────────────────────────────────── */
const mapCampRow = (row) => ({
  id: row.id,
  title: row.title || "",
  date: row.meeting_date || "",
  time: "",
  attendees: row.attendees || "",
  agenda: "",
  minutesRef: "",
});

// يدمج الحقول الغنية من النموذج فوق الصف المحفوظ لعرضها في الجلسة الحالية فقط.
const buildCampMeeting = (savedRow, form) => ({
  ...mapCampRow(savedRow),
  time: form.time || "",
  attendees: form.attendees || "",
  agenda: form.agenda || "",
  minutesRef: form.minutesRef || "",
});

/* ─────────────────────────────────────────────
   ✦ CampMeetingsView — CRUD حقيقي مربوط بالإصدار النشط
   ───────────────────────────────────────────── */
function CampMeetingsView({ activeEditionId, showToast }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_MEETING);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // module ديناميكي مربوط بالإصدار: كل نسخة سنوية لها اجتماعاتها المنفصلة.
  const meetingModule = `camp-${activeEditionId}`;

  // إعادة التحميل عند التركيب وعند تغيّر الإصدار النشط.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    getMeetings(meetingModule)
      .then((rows) => { if (alive) setMeetings((rows || []).map(mapCampRow)); })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "تعذّر جلب الاجتماعات";
        setError(msg);
        showToast(msg, "error");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingModule]);

  async function handleSave() {
    if (!form.title || !form.date) {
      showToast("العنوان والتاريخ مطلوبان", "error");
      return;
    }
    setSaving(true);
    try {
      const saved = await createMeeting({
        module: meetingModule,
        title: form.title,
        meeting_date: form.date,
        location: "",
        attendees: form.attendees || "",
        notes: "",
      });
      if (saved) setMeetings(prev => [buildCampMeeting(saved, form), ...prev]);
      setForm(INITIAL_MEETING);
      setShowAdd(false);
      showToast("تم إضافة الاجتماع بنجاح");
    } catch (e) {
      showToast(e?.message || "تعذّر حفظ الاجتماع", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`حذف الاجتماع "${row.title}"؟`)) return;
    setDeletingId(row.id);
    try {
      await deleteMeeting(row.id);
      setMeetings(prev => prev.filter(m => m.id !== row.id));
      showToast("تم حذف الاجتماع");
    } catch (e) {
      showToast(e?.message || "تعذّر حذف الاجتماع", "error");
    } finally {
      setDeletingId(null);
    }
  }

  const columns = [
    { key: "title",      label: "العنوان" },
    { key: "date",       label: "التاريخ", nowrap: true },
    { key: "time",       label: "الوقت",  nowrap: true, render: v => v || "—" },
    { key: "attendees",  label: "الحضور", render: v => v || "—" },
    { key: "agenda",     label: "جدول الأعمال", render: v => v || "—" },
    { key: "minutesRef", label: "المحضر", nowrap: true, render: v => v || "—" },
    {
      key: "_actions", label: "", nowrap: true,
      render: (_v, row) => (
        <button
          onClick={() => handleDelete(row)}
          disabled={deletingId === row.id}
          title="حذف"
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 32, height: 32, borderRadius: 8, border: "none", background: "transparent",
            color: C.danger, cursor: deletingId === row.id ? "default" : "pointer",
            opacity: deletingId === row.id ? 0.5 : 1,
          }}
        >
          {deletingId === row.id
            ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
            : <Trash2 size={16} />}
        </button>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ color: C.textLight, fontSize: 13 }}>
            {loading ? "جارٍ التحميل…" : `${meetings.length} اجتماع في النسخة النشطة`}
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)} disabled={loading}>إضافة اجتماع</Btn>
        </div>
      </Card>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 40, color: C.textLight }}>
          <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} />
          <span style={{ fontSize: 14 }}>جارٍ تحميل الاجتماعات…</span>
        </div>
      ) : error ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 40, color: C.danger }}>
          <AlertCircle size={18} />
          <span style={{ fontSize: 14 }}>{error}</span>
        </div>
      ) : (
        <DataTable columns={columns} data={meetings} />
      )}

      {showAdd && (
        <Modal title="إضافة اجتماع" onClose={() => setShowAdd(false)} width={620}>
          <Field label="العنوان" value={form.title} onChange={v => setForm({ ...form, title: v })} required />
          <div style={styles.formGrid2}>
            <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} required />
            <Field label="الوقت"   type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
          </div>
          <Field label="الحضور" value={form.attendees} onChange={v => setForm({ ...form, attendees: v })} placeholder="رؤساء اللجان، المشرفون..." />
          <Field label="جدول الأعمال" type="textarea" value={form.agenda} onChange={v => setForm({ ...form, agenda: v })} />
          <Field label="ملف المحضر" value={form.minutesRef} onChange={v => setForm({ ...form, minutesRef: v })} placeholder="اسم الملف المرجعي" />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)} disabled={saving}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus} disabled={saving}>{saving ? "جارٍ الحفظ…" : "حفظ"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Tasks Tab
   ───────────────────────────────────────────── */
function TasksTab({ activeEditionId, committees, tasks, setTasks, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_TASK);
  const [filterCommittee, setFilterCommittee] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const editionCommittees = committees.filter(c => c.editionId === activeEditionId);
  const filtered = tasks
    .filter(t => t.editionId === activeEditionId)
    .filter(t => filterCommittee === "all" || t.committeeId === filterCommittee)
    .filter(t => filterStatus === "all" || t.status === filterStatus);

  async function handleSave() {
    if (!form.title || !form.committeeId || !form.assigneeName) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampTask({
      edition_id: activeEditionId,
      committee_id: form.committeeId, title: form.title, assignee_id: form.assigneeName,
      priority: form.priority, start_date: form.startDate || null, due_date: form.dueDate || null,
      status: form.status, progress: Number(form.progress) || 0, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setTasks(prev => [mapTaskRow(data), ...prev]);
    setForm(INITIAL_TASK);
    setShowAdd(false);
    showToast("تم إضافة المهمة بنجاح");
  }

  const columns = [
    { key: "title",        label: "المهمة" },
    { key: "committeeId",  label: "اللجنة", render: v => {
        const c = committees.find(x => x.id === v);
        return c ? c.name : "—";
      }
    },
    { key: "assigneeName", label: "المسؤول" },
    { key: "priority",     label: "الأولوية", nowrap: true, render: v => <StatusBadge status={v} statuses={TASK_PRIORITIES} /> },
    { key: "dueDate",      label: "الاستحقاق", nowrap: true },
    { key: "progress",     label: "التقدم",   nowrap: true, render: v => `${v || 0}%` },
    { key: "status",       label: "الحالة",   nowrap: true, render: v => <StatusBadge status={v} statuses={TASK_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterCommittee} onChange={e => setFilterCommittee(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع اللجان</option>
              {editionCommittees.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {TASK_STATUSES.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة مهمة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة مهمة" onClose={() => setShowAdd(false)} width={680}>
          <Field label="عنوان المهمة" value={form.title} onChange={v => setForm({ ...form, title: v })} required />
          <div style={styles.formGrid2}>
            <Field
              label="اللجنة"
              type="select"
              value={form.committeeId}
              onChange={v => setForm({ ...form, committeeId: v })}
              options={editionCommittees.map(c => ({ value: c.id, label: c.name }))}
              required
            />
            <Field label="المسؤول" value={form.assigneeName} onChange={v => setForm({ ...form, assigneeName: v })} required />
            <Field label="الأولوية" type="select" value={form.priority} onChange={v => setForm({ ...form, priority: v })} options={TASK_PRIORITIES.map(p => ({ value: p.key, label: p.label }))} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={TASK_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
            <Field label="تاريخ البدء" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
            <Field label="تاريخ الاستحقاق" type="date" value={form.dueDate} onChange={v => setForm({ ...form, dueDate: v })} />
            <Field label="نسبة الإنجاز %" type="number" min="0" max="100" value={form.progress} onChange={v => setForm({ ...form, progress: v })} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Volunteers Tab
   ───────────────────────────────────────────── */
function VolunteersTab({ activeEditionId, committees, volunteers, setVolunteers, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_VOLUNTEER);
  const [filterCommittee, setFilterCommittee] = useState("all");
  const [search, setSearch] = useState("");

  const editionCommittees = committees.filter(c => c.editionId === activeEditionId);
  const filtered = volunteers
    .filter(v => v.editionId === activeEditionId)
    .filter(v => filterCommittee === "all" || v.committeeId === filterCommittee)
    .filter(v => !search || v.name.includes(search) || v.phone.includes(search));

  async function handleSave() {
    if (!form.name || !form.committeeId) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const committee = editionCommittees.find(c => c.id === form.committeeId);
    const { data, error } = await createCampVolunteer({
      edition_id: activeEditionId,
      name: form.name, gender: form.gender, phone: form.phone, email: form.email,
      committee_id: form.committeeId, committee_name: committee ? committee.name : "—",
      role: form.role, hours: Number(form.hours) || 0, join_date: form.joinDate || null,
      certificate_issued: form.certificateIssued, status: form.status,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setVolunteers(prev => [mapVolunteerRow(data), ...prev]);
    setForm(INITIAL_VOLUNTEER);
    setShowAdd(false);
    showToast("تم إضافة المتطوع بنجاح");
  }

  const columns = [
    { key: "name",          label: "الاسم", nowrap: true },
    { key: "gender",        label: "الجنس", nowrap: true },
    { key: "committeeName", label: "اللجنة" },
    { key: "role",          label: "الدور" },
    { key: "phone",         label: "الجوال", nowrap: true },
    { key: "hours",         label: "ساعات", nowrap: true, render: v => `${v || 0}` },
    { key: "certificateIssued", label: "شهادة", nowrap: true, render: v => v ? "نعم" : "—" },
    { key: "status",        label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={VOLUNTEER_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الجوال..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <select value={filterCommittee} onChange={e => setFilterCommittee(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع اللجان</option>
            {editionCommittees.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة متطوع</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة متطوع" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="الاسم" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="الجنس" type="select" value={form.gender} onChange={v => setForm({ ...form, gender: v })} options={GENDERS} />
            <Field label="الجوال" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            <Field label="البريد" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            <Field
              label="اللجنة"
              type="select"
              value={form.committeeId}
              onChange={v => setForm({ ...form, committeeId: v })}
              options={editionCommittees.map(c => ({ value: c.id, label: c.name }))}
              required
            />
            <Field label="الدور" type="select" value={form.role} onChange={v => setForm({ ...form, role: v })} options={VOLUNTEER_ROLES} />
            <Field label="ساعات التطوع" type="number" min="0" value={form.hours} onChange={v => setForm({ ...form, hours: v })} />
            <Field label="تاريخ الانضمام" type="date" value={form.joinDate} onChange={v => setForm({ ...form, joinDate: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={VOLUNTEER_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={form.certificateIssued}
              onChange={e => setForm({ ...form, certificateIssued: e.target.checked })}
              style={{ marginLeft: 8 }}
            />
            <span>تم إصدار شهادة المتطوع</span>
          </label>
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Procurement Tab
   ───────────────────────────────────────────── */
function ProcurementTab({ activeEditionId, vendors, procurement, setProcurement, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PROCUREMENT);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const editionVendors = vendors.filter(v => v.editionId === activeEditionId);
  const filtered = procurement
    .filter(p => p.editionId === activeEditionId)
    .filter(p => filterCategory === "all" || p.category === filterCategory)
    .filter(p => filterStatus === "all" || p.status === filterStatus);

  async function handleSave() {
    if (!form.item || !form.category) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const vendor = editionVendors.find(v => v.id === form.vendorId);
    const { data, error } = await createCampProcurement({
      edition_id: activeEditionId,
      item: form.item, category: form.category, quantity: Number(form.quantity) || 0,
      unit: form.unit, vendor_id: form.vendorId || null,
      vendor_name: vendor ? vendor.name : form.vendorName || "—",
      order_date: form.orderDate || null, delivery_date: form.deliveryDate || null,
      cost: Number(form.totalCost) || 0, status: form.status, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setProcurement(prev => [mapProcurementRow(data), ...prev]);
    setForm(INITIAL_PROCUREMENT);
    setShowAdd(false);
    showToast("تم إضافة الطلب بنجاح");
  }

  const columns = [
    { key: "item",         label: "الصنف" },
    { key: "category",     label: "الفئة", nowrap: true },
    { key: "quantity",     label: "الكمية", nowrap: true, render: (v, r) => `${(v || 0).toLocaleString("ar-SA")} ${r.unit || ""}` },
    { key: "vendorName",   label: "المورد" },
    { key: "deliveryDate", label: "التسليم", nowrap: true },
    { key: "totalCost",    label: "الإجمالي (ر.س)", nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "status",       label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={PROCUREMENT_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الفئات</option>
              {PROCUREMENT_CATEGORIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {PROCUREMENT_STATUSES.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة طلب</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة طلب توريد" onClose={() => setShowAdd(false)} width={680}>
          <Field label="الصنف" value={form.item} onChange={v => setForm({ ...form, item: v })} required />
          <div style={styles.formGrid2}>
            <Field label="الفئة" type="select" value={form.category} onChange={v => setForm({ ...form, category: v })} options={PROCUREMENT_CATEGORIES} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={PROCUREMENT_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
            <Field label="الكمية" type="number" min="0" value={form.quantity} onChange={v => setForm({ ...form, quantity: v })} />
            <Field label="الوحدة" value={form.unit} onChange={v => setForm({ ...form, unit: v })} placeholder="وجبة / قطعة / كرتون..." />
            <Field
              label="المورد"
              type="select"
              value={form.vendorId}
              onChange={v => setForm({ ...form, vendorId: v })}
              options={editionVendors.map(v => ({ value: v.id, label: v.name }))}
            />
            <Field label="إجمالي التكلفة" type="number" min="0" value={form.totalCost} onChange={v => setForm({ ...form, totalCost: v })} />
            <Field label="تاريخ الطلب"   type="date" value={form.orderDate}    onChange={v => setForm({ ...form, orderDate: v })} />
            <Field label="تاريخ التسليم" type="date" value={form.deliveryDate} onChange={v => setForm({ ...form, deliveryDate: v })} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Vendors Tab — Stars renderer
   ───────────────────────────────────────────── */
function StarRating({ value }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={13}
          color={C.accent}
          fill={i <= v ? C.accent : "transparent"}
        />
      ))}
    </span>
  );
}

function VendorsTab({ activeEditionId, vendors, setVendors, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_VENDOR);
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = vendors
    .filter(v => v.editionId === activeEditionId)
    .filter(v => filterType === "all" || v.type === filterType)
    .filter(v => !search || v.name.includes(search) || (v.service || "").includes(search));

  async function handleSave() {
    if (!form.name || !form.service) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampVendor({
      edition_id: activeEditionId,
      name: form.name, category: form.type, service: form.service,
      contact: form.contactPerson, phone: form.phone, email: form.email, id_or_cr: form.idOrCR,
      contract_total: Number(form.contractTotal) || 0,
      contract_start: form.contractStart || null, contract_end: form.contractEnd || null,
      rating: Number(form.rating) || 0, status: form.status, terms: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setVendors(prev => [mapVendorRow(data), ...prev]);
    setForm(INITIAL_VENDOR);
    setShowAdd(false);
    showToast("تم إضافة المورد/المندوب بنجاح");
  }

  const columns = [
    { key: "name",          label: "الاسم", nowrap: true },
    { key: "type",          label: "النوع", nowrap: true },
    { key: "service",       label: "الخدمة" },
    { key: "contactPerson", label: "المسؤول" },
    { key: "phone",         label: "الجوال", nowrap: true },
    { key: "contractTotal", label: "العقد (ر.س)", nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "rating",        label: "التقييم", nowrap: true, render: v => <StarRating value={v} /> },
    { key: "status",        label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={VENDOR_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الخدمة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الأنواع</option>
            {VENDOR_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة مورد/مندوب" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="الاسم"       value={form.name}     onChange={v => setForm({ ...form, name: v })} required />
            <Field label="النوع"       type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={VENDOR_TYPES} />
            <Field label="الخدمة"      value={form.service}  onChange={v => setForm({ ...form, service: v })} required />
            <Field label="المسؤول"     value={form.contactPerson} onChange={v => setForm({ ...form, contactPerson: v })} />
            <Field label="الجوال"      value={form.phone}    onChange={v => setForm({ ...form, phone: v })} />
            <Field label="البريد"      type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            <Field label="الهوية / السجل التجاري" value={form.idOrCR} onChange={v => setForm({ ...form, idOrCR: v })} />
            <Field label="قيمة العقد (ر.س)" type="number" min="0" value={form.contractTotal} onChange={v => setForm({ ...form, contractTotal: v })} />
            <Field label="بداية العقد" type="date" value={form.contractStart} onChange={v => setForm({ ...form, contractStart: v })} />
            <Field label="نهاية العقد" type="date" value={form.contractEnd}   onChange={v => setForm({ ...form, contractEnd: v })} />
            <Field label="التقييم (1-5)" type="number" min="1" max="5" value={form.rating} onChange={v => setForm({ ...form, rating: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={VENDOR_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Partners Tab
   ───────────────────────────────────────────── */
function PartnersTab({ activeEditionId, partners, setPartners, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PARTNER);
  const [filterType, setFilterType] = useState("all");

  const filtered = partners
    .filter(p => p.editionId === activeEditionId)
    .filter(p => filterType === "all" || p.type === filterType);

  async function handleSave() {
    if (!form.name || !form.type) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampPartner({
      edition_id: activeEditionId,
      name: form.name, type: form.type, contact: form.contactPerson, phone: form.phone,
      contribution: form.partnershipType, value_or_amount: Number(form.valueOrAmount) || 0,
      mou: form.mou, mou_ref: form.mouRef, status: form.status, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setPartners(prev => [mapPartnerRow(data), ...prev]);
    setForm(INITIAL_PARTNER);
    setShowAdd(false);
    showToast("تم إضافة الشريك بنجاح");
  }

  const columns = [
    { key: "name",            label: "الجهة" },
    { key: "type",            label: "النوع", nowrap: true },
    { key: "partnershipType", label: "نوع الشراكة", nowrap: true },
    { key: "valueOrAmount",   label: "القيمة (ر.س)", nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "contactPerson",   label: "المسؤول" },
    { key: "phone",           label: "الجوال", nowrap: true },
    { key: "mou",             label: "مذكرة تفاهم", nowrap: true, render: (v, r) => v ? (r.mouRef || "نعم") : "—" },
    { key: "status",          label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={PARTNER_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الأنواع</option>
            {PARTNER_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة جهة شريكة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة جهة شريكة" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="اسم الجهة"        value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="نوع الجهة"        type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={PARTNER_TYPES} />
            <Field label="المسؤول"          value={form.contactPerson} onChange={v => setForm({ ...form, contactPerson: v })} />
            <Field label="الجوال"           value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            <Field label="نوع الشراكة"      type="select" value={form.partnershipType} onChange={v => setForm({ ...form, partnershipType: v })} options={PARTNERSHIP_TYPES} />
            <Field label="القيمة (ر.س)"     type="number" min="0" value={form.valueOrAmount} onChange={v => setForm({ ...form, valueOrAmount: v })} />
            <Field label="رقم مذكرة التفاهم" value={form.mouRef} onChange={v => setForm({ ...form, mouRef: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={PARTNER_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <label style={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={form.mou}
              onChange={e => setForm({ ...form, mou: e.target.checked })}
              style={{ marginLeft: 8 }}
            />
            <span>توجد مذكرة تفاهم</span>
          </label>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Letters Tab
   ───────────────────────────────────────────── */
function LettersTab({ activeEditionId, letters, setLetters, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LETTER);
  const [filterDirection, setFilterDirection] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = letters
    .filter(l => l.editionId === activeEditionId)
    .filter(l => filterDirection === "all" || l.direction === filterDirection)
    .filter(l => filterStatus === "all" || l.status === filterStatus)
    .filter(l => !search || (l.subject || "").includes(search) || (l.refNumber || "").includes(search));

  async function handleSave() {
    if (!form.refNumber || !form.date || !form.subject) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampLetter({
      edition_id: activeEditionId,
      ref_number: form.refNumber, sent_date: form.date || null, direction: form.direction,
      party: form.recipient, sender: form.sender, subject: form.subject, summary: form.summary,
      attachment_ref: form.attachmentRef, status: form.status, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setLetters(prev => [mapLetterRow(data), ...prev]);
    setForm(INITIAL_LETTER);
    setShowAdd(false);
    showToast("تم إضافة الخطاب بنجاح");
  }

  const columns = [
    { key: "refNumber", label: "الرقم", nowrap: true },
    { key: "date",      label: "التاريخ", nowrap: true },
    { key: "direction", label: "الاتجاه", nowrap: true },
    { key: "subject",   label: "الموضوع" },
    { key: "recipient", label: "إلى" },
    { key: "sender",    label: "من" },
    { key: "status",    label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={LETTER_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالموضوع أو الرقم..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterDirection} onChange={e => setFilterDirection(e.target.value)} style={styles.filterSelect}>
              <option value="all">صادر/وارد</option>
              {LETTER_DIRECTIONS.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {LETTER_STATUSES.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة خطاب</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة خطاب" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="الرقم المرجعي" value={form.refNumber} onChange={v => setForm({ ...form, refNumber: v })} required />
            <Field label="التاريخ"      type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} required />
            <Field label="الاتجاه"      type="select" value={form.direction} onChange={v => setForm({ ...form, direction: v })} options={LETTER_DIRECTIONS} />
            <Field label="الحالة"       type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={LETTER_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
            <Field label="من (المرسل)"  value={form.sender}    onChange={v => setForm({ ...form, sender: v })} />
            <Field label="إلى (المستلم)" value={form.recipient} onChange={v => setForm({ ...form, recipient: v })} />
          </div>
          <Field label="الموضوع" value={form.subject} onChange={v => setForm({ ...form, subject: v })} required />
          <Field label="ملخص"     type="textarea" value={form.summary} onChange={v => setForm({ ...form, summary: v })} />
          <Field label="مرفق (اسم الملف)" value={form.attachmentRef} onChange={v => setForm({ ...form, attachmentRef: v })} />
          <Field label="ملاحظات"  type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Permits Tab — expiry coloring from TODAY_REF
   ───────────────────────────────────────────── */
function getExpiryColor(expiryDate, status) {
  if (!expiryDate) return C.textLight;
  if (status === "منتهي") return C.danger;
  const exp = new Date(expiryDate);
  if (isNaN(exp.getTime())) return C.textLight;
  const days = Math.round((exp - TODAY_REF) / (1000 * 60 * 60 * 24));
  if (days < 0)  return C.danger;
  if (days <= 30) return "#ea580c";
  if (days <= 90) return C.accent;
  return C.success;
}

function PermitsTab({ activeEditionId, permits, setPermits, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PERMIT);
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = permits
    .filter(p => p.editionId === activeEditionId)
    .filter(p => filterStatus === "all" || p.status === filterStatus);

  async function handleSave() {
    if (!form.type || !form.authority) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampPermit({
      edition_id: activeEditionId,
      type: form.type, issuer: form.authority, permit_number: form.permitNumber,
      issued_at: form.issueDate || null, expiry_date: form.expiryDate || null,
      fees: Number(form.fees) || 0, status: form.status, conditions: form.conditions, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setPermits(prev => [mapPermitRow(data), ...prev]);
    setForm(INITIAL_PERMIT);
    setShowAdd(false);
    showToast("تم إضافة الترخيص بنجاح");
  }

  const columns = [
    { key: "type",         label: "النوع", nowrap: true },
    { key: "authority",    label: "الجهة المانحة" },
    { key: "permitNumber", label: "رقم الترخيص", nowrap: true, render: v => v || "—" },
    { key: "issueDate",    label: "تاريخ الإصدار", nowrap: true, render: v => v || "—" },
    { key: "expiryDate",   label: "تاريخ الانتهاء", nowrap: true, render: (v, r) => (
        v ? <span style={{ color: getExpiryColor(v, r.status), fontWeight: 700 }}>{v}</span> : "—"
      )
    },
    { key: "fees",         label: "الرسوم", nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "status",       label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={PERMIT_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الحالات</option>
            {PERMIT_STATUSES.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة ترخيص</Btn>
        </div>
        <p style={styles.helperText}>
          الألوان: أخضر = أكثر من 90 يوم على الانتهاء — ذهبي ≤ 90 — برتقالي ≤ 30 — أحمر منتهي.
        </p>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة ترخيص" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="نوع الترخيص"   type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={PERMIT_TYPES} />
            <Field label="الجهة المانحة" value={form.authority} onChange={v => setForm({ ...form, authority: v })} required />
            <Field label="رقم الترخيص"   value={form.permitNumber} onChange={v => setForm({ ...form, permitNumber: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={PERMIT_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
            <Field label="تاريخ الإصدار" type="date" value={form.issueDate}  onChange={v => setForm({ ...form, issueDate: v })} />
            <Field label="تاريخ الانتهاء" type="date" value={form.expiryDate} onChange={v => setForm({ ...form, expiryDate: v })} />
            <Field label="الرسوم (ر.س)"  type="number" min="0" value={form.fees} onChange={v => setForm({ ...form, fees: v })} />
          </div>
          <Field label="الشروط"   type="textarea" value={form.conditions} onChange={v => setForm({ ...form, conditions: v })} />
          <Field label="ملاحظات"  type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Dawah Programs Tab
   ───────────────────────────────────────────── */
function DawahProgramsTab({ activeEditionId, locations, dawahPrograms, setDawahPrograms, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_DAWAH_PROGRAM);
  const [filterType, setFilterType] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");

  const editionLocations = locations.filter(l => l.editionId === activeEditionId);
  const filtered = dawahPrograms
    .filter(p => p.editionId === activeEditionId)
    .filter(p => filterType === "all" || p.type === filterType)
    .filter(p => filterLocation === "all" || p.locationId === filterLocation);

  async function handleSave() {
    if (!form.name || !form.type || !form.date) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const loc = editionLocations.find(l => l.id === form.locationId);
    const { data, error } = await createCampDawahProgram({
      edition_id: activeEditionId,
      name: form.name, type: form.type, speaker: form.speaker,
      location_id: form.locationId || null, location_name: loc ? loc.name : form.locationName || "—",
      date: form.date || null, time: form.time || null,
      attendees_count: Number(form.attendees) || 0, new_muslims_count: Number(form.newMuslimsCount) || 0,
      notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setDawahPrograms(prev => [mapDawahProgramRow(data), ...prev]);
    setForm(INITIAL_DAWAH_PROGRAM);
    setShowAdd(false);
    showToast("تم إضافة البرنامج بنجاح");
  }

  const columns = [
    { key: "name",            label: "البرنامج" },
    { key: "type",            label: "النوع", nowrap: true },
    { key: "speaker",         label: "المتحدث" },
    { key: "locationName",    label: "الموقع" },
    { key: "date",            label: "التاريخ", nowrap: true },
    { key: "time",            label: "الوقت",   nowrap: true },
    { key: "attendees",       label: "الحضور",  nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "newMuslimsCount", label: "إسلام جدد", nowrap: true },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأنواع</option>
              {DAWAH_PROGRAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع المواقع</option>
              {editionLocations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة برنامج</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة برنامج دعوي" onClose={() => setShowAdd(false)} width={680}>
          <Field label="اسم البرنامج" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
          <div style={styles.formGrid2}>
            <Field label="النوع" type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={DAWAH_PROGRAM_TYPES} />
            <Field label="المتحدث" value={form.speaker} onChange={v => setForm({ ...form, speaker: v })} />
            <Field
              label="الموقع"
              type="select"
              value={form.locationId}
              onChange={v => setForm({ ...form, locationId: v })}
              options={editionLocations.map(l => ({ value: l.id, label: l.name }))}
            />
            <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} required />
            <Field label="الوقت" type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
            <Field label="عدد الحضور" type="number" min="0" value={form.attendees} onChange={v => setForm({ ...form, attendees: v })} />
            <Field label="عدد المسلمين الجدد" type="number" min="0" value={form.newMuslimsCount} onChange={v => setForm({ ...form, newMuslimsCount: v })} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ New Muslims Tab
   ───────────────────────────────────────────── */
function NewMuslimsTab({ activeEditionId, locations, dawahPrograms, newMuslims, setNewMuslims, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_NEW_MUSLIM);
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const editionLocations = locations.filter(l => l.editionId === activeEditionId);
  const editionPrograms  = dawahPrograms.filter(p => p.editionId === activeEditionId);

  const filtered = newMuslims
    .filter(n => n.editionId === activeEditionId)
    .filter(n => filterStatus === "all" || n.followUpStatus === filterStatus)
    .filter(n => !search || (n.nameAfter || "").includes(search) || (n.nameBefore || "").includes(search) || (n.nationality || "").includes(search));

  async function handleSave() {
    if (!form.nameAfter || !form.conversionDate) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const loc = editionLocations.find(l => l.id === form.locationId);
    const prog = editionPrograms.find(p => p.id === form.programId);
    const { data, error } = await createCampNewMuslim({
      edition_id: activeEditionId,
      name_before: form.nameBefore, name: form.nameAfter, gender: form.gender,
      nationality: form.nationality, shahada_date: form.conversionDate || null,
      location_id: form.locationId || null, location_name: loc ? loc.name : form.locationName || "—",
      program_id: form.programId || null, program_name: prog ? prog.name : form.programName || "—",
      preacher: form.preacher, certificate_issued: form.certificateIssued,
      follow_up_status: form.followUpStatus, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setNewMuslims(prev => [mapNewMuslimRow(data), ...prev]);
    setForm(INITIAL_NEW_MUSLIM);
    setShowAdd(false);
    showToast("تم إضافة المسلم الجديد بنجاح");
  }

  const columns = [
    { key: "nameAfter",         label: "الاسم بعد الإسلام", nowrap: true },
    { key: "nameBefore",        label: "الاسم السابق" },
    { key: "gender",            label: "الجنس", nowrap: true },
    { key: "nationality",       label: "الجنسية", nowrap: true },
    { key: "conversionDate",    label: "تاريخ الإسلام", nowrap: true },
    { key: "locationName",      label: "الموقع" },
    { key: "preacher",          label: "الداعية" },
    { key: "certificateIssued", label: "الشهادة", nowrap: true, render: v => v ? "صدرت" : "—" },
    { key: "followUpStatus",    label: "المتابعة", nowrap: true, render: v => <StatusBadge status={v} statuses={NEW_MUSLIM_FOLLOWUP_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الجنسية..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع حالات المتابعة</option>
            {NEW_MUSLIM_FOLLOWUP_STATUSES.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة مسلم جديد</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة مسلم جديد" onClose={() => setShowAdd(false)} width={720}>
          <div style={styles.formGrid2}>
            <Field label="الاسم بعد الإسلام" value={form.nameAfter}  onChange={v => setForm({ ...form, nameAfter: v })} required />
            <Field label="الاسم السابق"      value={form.nameBefore} onChange={v => setForm({ ...form, nameBefore: v })} />
            <Field label="الجنس" type="select" value={form.gender} onChange={v => setForm({ ...form, gender: v })} options={GENDERS} />
            <Field label="الجنسية" value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} />
            <Field label="تاريخ الإسلام" type="date" value={form.conversionDate} onChange={v => setForm({ ...form, conversionDate: v })} required />
            <Field
              label="الموقع"
              type="select"
              value={form.locationId}
              onChange={v => setForm({ ...form, locationId: v })}
              options={editionLocations.map(l => ({ value: l.id, label: l.name }))}
            />
            <Field
              label="البرنامج"
              type="select"
              value={form.programId}
              onChange={v => setForm({ ...form, programId: v })}
              options={editionPrograms.map(p => ({ value: p.id, label: p.name }))}
            />
            <Field label="الداعية" value={form.preacher} onChange={v => setForm({ ...form, preacher: v })} />
            <Field
              label="حالة المتابعة"
              type="select"
              value={form.followUpStatus}
              onChange={v => setForm({ ...form, followUpStatus: v })}
              options={NEW_MUSLIM_FOLLOWUP_STATUSES.map(s => ({ value: s.key, label: s.label }))}
            />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13, color: C.primary }}>
            <input
              type="checkbox"
              checked={form.certificateIssued}
              onChange={e => setForm({ ...form, certificateIssued: e.target.checked })}
            />
            <span>صدرت شهادة الإسلام</span>
          </label>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Official Visits Tab
   ───────────────────────────────────────────── */
function OfficialVisitsTab({ activeEditionId, officialVisits, setOfficialVisits, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_OFFICIAL_VISIT);

  const filtered = officialVisits.filter(v => v.editionId === activeEditionId);

  async function handleSave() {
    if (!form.visitorName || !form.date) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampOfficialVisit({
      edition_id: activeEditionId,
      visitor: form.visitorName, visitor_title: form.visitorTitle, organization: form.organization,
      visit_date: form.date || null, time: form.time || null, host_name: form.hostName,
      purpose: form.purpose, summary: form.outcome, media_coverage: form.mediaCoverage, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setOfficialVisits(prev => [mapOfficialVisitRow(data), ...prev]);
    setForm(INITIAL_OFFICIAL_VISIT);
    setShowAdd(false);
    showToast("تم تسجيل الزيارة بنجاح");
  }

  const columns = [
    { key: "visitorName",   label: "الزائر",       nowrap: true },
    { key: "visitorTitle",  label: "الصفة" },
    { key: "organization",  label: "الجهة" },
    { key: "date",          label: "التاريخ",      nowrap: true },
    { key: "time",          label: "الوقت",        nowrap: true },
    { key: "hostName",      label: "المستقبِل" },
    { key: "purpose",       label: "الغرض" },
    { key: "outcome",       label: "النتيجة" },
    { key: "mediaCoverage", label: "تغطية إعلامية", nowrap: true, render: v => v ? "نعم" : "—" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ color: C.textLight, fontSize: 13 }}>
            {filtered.length} زيارة في النسخة النشطة
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>تسجيل زيارة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="تسجيل زيارة رسمية" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="اسم الزائر" value={form.visitorName} onChange={v => setForm({ ...form, visitorName: v })} required />
            <Field label="الصفة"       value={form.visitorTitle} onChange={v => setForm({ ...form, visitorTitle: v })} />
            <Field label="الجهة"       value={form.organization} onChange={v => setForm({ ...form, organization: v })} />
            <Field label="المستقبِل"   value={form.hostName} onChange={v => setForm({ ...form, hostName: v })} />
            <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} required />
            <Field label="الوقت"   type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
          </div>
          <Field label="الغرض"   value={form.purpose} onChange={v => setForm({ ...form, purpose: v })} />
          <Field label="النتيجة" value={form.outcome} onChange={v => setForm({ ...form, outcome: v })} />
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", fontSize: 13, color: C.primary }}>
            <input
              type="checkbox"
              checked={form.mediaCoverage}
              onChange={e => setForm({ ...form, mediaCoverage: e.target.checked })}
            />
            <span>تغطية إعلامية</span>
          </label>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Reports Tab
   ───────────────────────────────────────────── */
function ReportsTab({ editions, activeEdition, locations, committees, volunteers, procurement, dawahPrograms, newMuslims, showToast }) {
  const editionId = activeEdition?.id;
  const locsCount  = locations.filter(l => l.editionId === editionId).length;
  const comsCount  = committees.filter(c => c.editionId === editionId).length;
  const volsCount  = volunteers.filter(v => v.editionId === editionId).length;
  const dwhCount   = dawahPrograms.filter(p => p.editionId === editionId).length;
  const nmusCount  = newMuslims.filter(n => n.editionId === editionId).length;
  const totalCost  = procurement.filter(p => p.editionId === editionId).reduce((s, p) => s + (Number(p.totalCost) || 0), 0);

  const editionsSummary = editions.map(ed => ({
    title: ed.title,
    newMuslims: newMuslims.filter(n => n.editionId === ed.id).length,
    programs:   dawahPrograms.filter(p => p.editionId === ed.id).length,
    volunteers: volunteers.filter(v => v.editionId === ed.id).length,
  }));

  const cards = [
    {
      key: "edition",
      title: "تقرير النسخة الشامل",
      icon: FileText,
      color: C.teal,
      summary: `${comsCount} لجنة · ${locsCount} موقع · ${volsCount} متطوع · ${dwhCount} برنامج · ${nmusCount} مسلم جديد`,
    },
    {
      key: "committee",
      title: "تقرير لجنة محددة",
      icon: Users,
      color: "#7c3aed",
      summary: `${comsCount} لجنة نشطة — اختر اللجنة عند التوليد`,
    },
    {
      key: "newMuslims",
      title: "تقرير المسلمين الجدد",
      icon: UserPlus,
      color: C.success,
      summary: `${nmusCount} مسلم جديد في النسخة النشطة`,
    },
    {
      key: "finance",
      title: "التقرير المالي",
      icon: BarChart3,
      color: C.accent,
      summary: `إجمالي مشتريات النسخة: ${totalCost.toLocaleString("ar-SA")} ر.س`,
    },
    {
      key: "comparison",
      title: "تقرير المقارنة بين النسخ",
      icon: Archive,
      color: "#0891b2",
      summary: editionsSummary
        .map(s => `${s.title}: ${s.newMuslims} م.ج / ${s.programs} ب / ${s.volunteers} مت`)
        .join(" — "),
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.primary, fontSize: 14, fontWeight: 700 }}>
          <FileText size={18} color={C.teal} />
          <span>توليد تقارير النسخة النشطة: {activeEdition?.title || "—"}</span>
        </div>
        <p style={styles.helperText}>
          الإحصاءات أدناه محسوبة لحظياً من بيانات النسخة النشطة. اضغط "توليد" لإعداد ملف التقرير.
        </p>
      </Card>

      <div style={styles.reportsGrid}>
        {cards.map(c => {
          const IconCmp = c.icon;
          return (
            <Card key={c.key} style={styles.reportCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ ...styles.reportIcon, background: `${c.color}20`, color: c.color }}>
                  <IconCmp size={22} color={c.color} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>{c.title}</div>
              </div>
              <p style={{ fontSize: 12, color: C.textLight, margin: "4px 0 14px", lineHeight: 1.7 }}>
                {c.summary}
              </p>
              <Btn icon={Download} onClick={() => showToast(`تم تجهيز: ${c.title}`)}>توليد</Btn>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Library & Archive Tab
   ───────────────────────────────────────────── */
function LibraryTab({ activeEditionId, library, setLibrary, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LIBRARY);
  const [filterCategory, setFilterCategory] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = library
    .filter(l => l.editionId === activeEditionId)
    .filter(l => filterCategory === "all" || l.category === filterCategory)
    .filter(l => !search || (l.title || "").includes(search) || (l.fileRef || "").includes(search));

  async function handleSave() {
    if (!form.title || !form.category) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampLibraryItem({
      edition_id: activeEditionId,
      title: form.title, type: form.category, upload_date: form.uploadDate || null,
      author: form.uploader, file_ref: form.fileRef, size: form.size,
      downloads: Number(form.downloads) || 0, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setLibrary(prev => [mapLibraryRow(data), ...prev]);
    setForm(INITIAL_LIBRARY);
    setShowAdd(false);
    showToast("تم إضافة الملف بنجاح");
  }

  const columns = [
    { key: "title",      label: "العنوان" },
    { key: "category",   label: "الفئة",     nowrap: true },
    { key: "uploadDate", label: "تاريخ الرفع", nowrap: true },
    { key: "uploader",   label: "بواسطة" },
    { key: "fileRef",    label: "المرجع",     nowrap: true, render: v => v || "—" },
    { key: "size",       label: "الحجم",      nowrap: true },
    { key: "downloads",  label: "تنزيلات",    nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالعنوان أو اسم الملف..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={styles.filterSelect}>
            <option value="all">جميع الفئات</option>
            {LIBRARY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة ملف</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة ملف للأرشيف" onClose={() => setShowAdd(false)} width={680}>
          <Field label="العنوان" value={form.title} onChange={v => setForm({ ...form, title: v })} required />
          <div style={styles.formGrid2}>
            <Field label="الفئة" type="select" value={form.category} onChange={v => setForm({ ...form, category: v })} options={LIBRARY_CATEGORIES} />
            <Field label="تاريخ الرفع" type="date" value={form.uploadDate} onChange={v => setForm({ ...form, uploadDate: v })} />
            <Field label="بواسطة"      value={form.uploader} onChange={v => setForm({ ...form, uploader: v })} />
            <Field label="مرجع الملف"  value={form.fileRef} onChange={v => setForm({ ...form, fileRef: v })} placeholder="filename.pdf" />
            <Field label="الحجم"       value={form.size} onChange={v => setForm({ ...form, size: v })} placeholder="2.5 MB" />
            <Field label="عدد التنزيلات" type="number" min="0" value={form.downloads} onChange={v => setForm({ ...form, downloads: v })} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ✦ Lessons Learned Tab
   ───────────────────────────────────────────── */
function LessonsLearnedTab({ activeEditionId, lessonsLearned, setLessonsLearned, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_LESSON);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = lessonsLearned
    .filter(l => l.editionId === activeEditionId)
    .filter(l => filterCategory === "all" || l.category === filterCategory)
    .filter(l => filterStatus === "all" || l.status === filterStatus);

  async function handleSave() {
    if (!form.title || !form.category) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const { data, error } = await createCampLessonLearned({
      edition_id: activeEditionId,
      category: form.category, title: form.title, situation: form.situation,
      lesson: form.lesson, recommendation: form.recommendation, recorded_by: form.owner,
      status: form.status, notes: form.notes,
    });
    if (error) { showToast(error, "error"); return; }
    if (data) setLessonsLearned(prev => [mapLessonRow(data), ...prev]);
    setForm(INITIAL_LESSON);
    setShowAdd(false);
    showToast("تم تسجيل الدرس بنجاح");
  }

  const columns = [
    { key: "title",          label: "العنوان" },
    { key: "category",       label: "الفئة",      nowrap: true },
    { key: "situation",      label: "الموقف" },
    { key: "lesson",         label: "الدرس" },
    { key: "recommendation", label: "التوصية" },
    { key: "owner",          label: "المسؤول" },
    { key: "status",         label: "الحالة",     nowrap: true, render: v => <StatusBadge status={v} statuses={LESSON_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الفئات</option>
              {LESSON_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {LESSON_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة درس</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة درس مستفاد" onClose={() => setShowAdd(false)} width={680}>
          <Field label="العنوان" value={form.title} onChange={v => setForm({ ...form, title: v })} required />
          <div style={styles.formGrid2}>
            <Field label="الفئة"  type="select" value={form.category} onChange={v => setForm({ ...form, category: v })} options={LESSON_CATEGORIES} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={LESSON_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
            <Field label="المسؤول" value={form.owner} onChange={v => setForm({ ...form, owner: v })} />
          </div>
          <Field label="الموقف"   type="textarea" value={form.situation} onChange={v => setForm({ ...form, situation: v })} />
          <Field label="الدرس"    type="textarea" value={form.lesson}    onChange={v => setForm({ ...form, lesson: v })} />
          <Field label="التوصية"  type="textarea" value={form.recommendation} onChange={v => setForm({ ...form, recommendation: v })} />
          <Field label="ملاحظات"  type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  editionCard: { padding: 18, marginBottom: 18 },
  editionRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    gap: 16, flexWrap: "wrap",
  },
  editionInfo: { display: "flex", alignItems: "center", gap: 12 },
  editionLabel: { fontSize: 12, color: C.textLight, marginBottom: 4 },
  editionTitle: { fontSize: 16, fontWeight: 700, color: C.primary, display: "flex", alignItems: "center", gap: 8 },
  editionControls: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  editionSelect: {
    padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif",
    color: C.primary, background: C.white, cursor: "pointer", minWidth: 240,
  },

  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 20, marginBottom: 16,
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20, marginBottom: 20,
  },
  chartCard: { padding: 20 },
  chartHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16,
  },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: {
    display: "flex", flexWrap: "wrap", gap: 12,
    justifyContent: "center", marginTop: 8,
  },
  legendItem: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "4px 10px", background: C.bg, borderRadius: 8,
  },
  emptyChart: {
    height: 240, display: "flex", alignItems: "center", justifyContent: "center",
    color: C.textLight, fontSize: 13,
  },

  toolbar: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`,
    flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif",
    color: C.primary, background: C.white, cursor: "pointer",
  },

  formGrid2: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12, marginBottom: 8,
  },
  modalActions: {
    display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18,
  },
  sectionDivider: {
    marginTop: 16, paddingBottom: 6, marginBottom: 8,
    borderBottom: `2px solid ${C.teal}30`,
    fontSize: 13, fontWeight: 700, color: C.teal,
  },
  helperText: { fontSize: 12, color: C.textLight, margin: "4px 0 12px", lineHeight: 1.6 },
  checkboxGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8,
  },
  checkboxRow: {
    display: "flex", alignItems: "center",
    padding: "8px 12px", background: C.bg, borderRadius: 8,
    fontSize: 13, color: C.primary, border: `1px solid ${C.border}`,
  },

  detailGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10, marginTop: 8, fontSize: 13, color: C.primary,
  },
  dimLabel: { color: C.textLight, marginLeft: 4 },

  reportsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
    gap: 16,
  },
  reportCard: { padding: 18, display: "flex", flexDirection: "column" },
  reportIcon: {
    width: 44, height: 44, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
};
